import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

enum Theme {
  light = 'light',
  dark = 'dark',
}

// Separate contexts for state and actions to optimize re-renders
interface ThemeStateContextType {
  theme: Theme;
  isDark: boolean;
}

interface ThemeActionsContextType {
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeStateContext = createContext<ThemeStateContextType | undefined>(
  undefined,
);
const ThemeActionsContext = createContext<ThemeActionsContextType | undefined>(
  undefined,
);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, then system preference
    const stored = localStorage.getItem('theme');
    if (stored === Theme.light || stored === Theme.dark) {
      return stored;
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return Theme.dark;
    }

    return Theme.light;
  });

  useEffect(() => {
    const root = document.documentElement;

    if (theme === Theme.dark) {
      root.classList.add(Theme.dark);
    } else {
      root.classList.remove(Theme.dark);
    }

    localStorage.setItem('theme', theme);
  }, [theme]);

  // Memoize the toggle function to prevent unnecessary re-renders
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === Theme.light ? Theme.dark : Theme.light));
  }, []);

  // Memoize the state to prevent unnecessary re-renders
  const stateValue = useMemo(
    () => ({
      theme,
      isDark: theme === Theme.dark,
    }),
    [theme],
  );

  // Memoize the actions to prevent unnecessary re-renders
  const actionsValue = useMemo(
    () => ({
      setTheme,
      toggleTheme,
    }),
    [toggleTheme],
  );

  return (
    <ThemeStateContext.Provider value={stateValue}>
      <ThemeActionsContext.Provider value={actionsValue}>
        {children}
      </ThemeActionsContext.Provider>
    </ThemeStateContext.Provider>
  );
}

// Hook for accessing theme state only
export function useThemeState() {
  const context = useContext(ThemeStateContext);
  if (context === undefined) {
    throw new Error('useThemeState must be used within a ThemeProvider');
  }
  return context;
}

// Hook for accessing theme actions only
export function useThemeActions() {
  const context = useContext(ThemeActionsContext);
  if (context === undefined) {
    throw new Error('useThemeActions must be used within a ThemeProvider');
  }
  return context;
}
